// The operating cadence's reads (MEETINGS): the issues board, the meeting series, one meeting
// and the vision page. Every key sits under `["company", co, "cadence"]`, the root the change
// pings of `issue`, `meeting`, `meeting_series` and `decision` refresh (live/change-pings.ts).
import { queryOptions, useQuery } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { orgChartQuery } from "../chart/chart-queries";

export const cadenceKeys = {
  root: (co: string) => ["company", co, "cadence"] as const,
  issues: (co: string) => ["company", co, "cadence", "issues"] as const,
  issueList: (co: string, view: IssueListView) =>
    ["company", co, "cadence", "issues", view] as const,
  series: (co: string) => ["company", co, "cadence", "series"] as const,
  seriesMeetings: (co: string, id: string) =>
    ["company", co, "cadence", "series", id, "meetings"] as const,
  meeting: (co: string, id: string) => ["company", co, "cadence", "meeting", id] as const,
  vision: (co: string) => ["company", co, "cadence", "vision"] as const,
};

/** The board (live issues), the last 30 days' closed ones as well, or the archived ones. */
export type IssueListView = "live" | "closed" | "archived";

export const issuesQuery = (api: Api, co: string, view: IssueListView = "live") =>
  queryOptions({
    queryKey: cadenceKeys.issueList(co, view),
    queryFn: () =>
      api.client.issues.list({
        params: { co },
        query:
          view === "closed"
            ? { include_closed: "true" }
            : view === "archived"
              ? { archived: "true" }
              : {},
      }),
    staleTime: 15_000,
  });

export const seriesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: cadenceKeys.series(co),
    queryFn: () => api.client.meetingSeries.list({ params: { co } }),
    staleTime: 15_000,
  });

export const seriesMeetingsQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: cadenceKeys.seriesMeetings(co, id),
    queryFn: () => api.client.meetings.list({ params: { co, id } }),
  });

export const meetingQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: cadenceKeys.meeting(co, id),
    queryFn: () => api.client.meetings.get({ params: { co, id } }),
  });

export const visionQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: cadenceKeys.vision(co),
    queryFn: () => api.client.vision.get({ params: { co } }),
  });

export type SeatOption = { id: string; title: string; holder: string | null; label: string };

/** The company's open seats as pickers list them ("Recruiting lead · Jordan Lee"). */
export function useSeatOptions(api: Api, co: string): SeatOption[] {
  const chart = useQuery({ ...orgChartQuery(api, co), retry: false });
  return (chart.data?.seats ?? [])
    .filter((s) => s.status !== "closed")
    .map((s) => {
      const holder =
        [...s.holders].sort((a, b) =>
          a.membership_kind === b.membership_kind ? 0 : a.membership_kind === "person" ? -1 : 1,
        )[0]?.display_name ?? null;
      return {
        id: s.id,
        title: s.title,
        holder,
        label: holder ? `${s.title} · ${holder}` : s.title,
      };
    });
}
